import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Close } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Checkbox from "../../../components/Checkbox";
import Input from "../../../components/Input";
import messages from "../../../assets/locale/messages";
import { RedCloseBgIcon } from "../../../utils/Icons";
import { rejectProductRequest } from "../../../store/Products/actions";

const RejectProduct = ({ productId, productName }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const lang = useSelector((state) => state.locale.lang);
  const {
    products: { reviewProduct }
  } = messages[lang];

  const [openModal, setOpenModal] = useState(false);

  const {
    values,
    setFieldValue,
    handleSubmit,
    setFieldTouched,
    dirty,
    isValid,
    touched,
    errors
  } = useFormik({
    initialValues: {
      defaultReason: false,
      rejectionReason: ""
    },
    validationSchema: Yup.object().shape(
      {
        defaultReason: Yup.boolean().when("rejectionReason", {
          is: (value) => !value,
          then: (schema) => schema.required(""),
          otherwise: (schema) => schema
        }),
        rejectionReason: Yup.string()
          .max(100, "rejectionReasonMaxChar")
          .when("defaultReason", {
            is: (value) => !value,
            then: (schema) => schema.required(""),
            otherwise: (schema) => schema
          })
      },
      ["defaultReason", "rejectionReason"]
    ),
    onSubmit: ({ defaultReason, rejectionReason }) => {
      dispatch(
        rejectProductRequest({
          data: {
            Id: productId,
            defaultReason,
            rejectionReason: rejectionReason?.trim()
          },
          navigate
        })
      );
      setOpenModal(false);
    }
  });

  const renderRejectionForm = () => (
    <form onSubmit={handleSubmit} noValidate>
      <Checkbox
        id="defaultReason"
        name="defaultReason"
        label={reviewProduct.rejectionReason}
        labelClass="title-color fsize-14 fweight-400"
        checkboxClass="mb-3"
        checked={values.defaultReason}
        onChange={(checked) => {
          setFieldTouched("defaultReason");
          setFieldValue("defaultReason", checked);
        }}
      />
      <Input
        id="rejectionReason"
        name="rejectionReason"
        label={reviewProduct.otherReasons}
        placeholder={reviewProduct.typeHere}
        onChange={(value) => {
          setFieldTouched("rejectionReason");
          setFieldValue("rejectionReason", value);
        }}
        value={values.rejectionReason}
        multiline
        rows={2}
        isInputHasErr={
          !!(touched.rejectionReason && errors.rejectionReason)
        }
        errMsg={errors.rejectionReason}
      />
    </form>
  );

  return (
    <div className="reject-request-modal">
      <Button
        label={
          <span className="d-flex align-items-center">
            <Close className="me-0 me-md-2" color="error" />
            <span>{reviewProduct.reject}</span>
          </span>
        }
        outlined
        reversedcolor
        danger
        className={`me-3`}
        onClick={() => setOpenModal(true)}
      />
      {openModal && (
        <Modal
          open={openModal}
          handleClose={() => setOpenModal(false)}
          title={
            <>
              {reviewProduct.rejectMsg} {productName}
            </>
          }
          content={renderRejectionForm()}
          isContentText={false}
          confirmBtnTxt={reviewProduct.reject}
          cancelBtnTxt={reviewProduct.cancel}
          icon={<RedCloseBgIcon />}
          handleConfirm={handleSubmit}
          handleCancel={() => setOpenModal(false)}
          disableConfirmBtn={!dirty || !isValid}
          confirmBtnProps={{ danger: true }}
        />
      )}
    </div>
  );
};

export default RejectProduct;

RejectProduct.propTypes = {
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  productName: PropTypes.string.isRequired
};
